/** PP/BP 営業進捗画面の共通ユーティリティ */

export function getCurrentYearMonth(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

export function buildRecentMonths(count = 13, fromDate = new Date()) {
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(fromDate.getFullYear(), fromDate.getMonth() - i, 1);
    return getCurrentYearMonth(d);
  });
}

export function formatYearMonthLabel(monthValue) {
  if (!monthValue || monthValue === '未定') return '未定';
  const parts = String(monthValue).split('-');
  if (parts.length !== 2) return String(monthValue);
  return `${parts[0]}年${Number(parts[1])}月`;
}

export function normalizeSalesStatus(status) {
  if (status === 'お見送り') return '見送り';
  return status || '';
}

export function getStatusBaseLabel(status) {
  if (!status) return '—';
  return String(status).replace(/（1\/2）/g, '').replace(/（2\/2）/g, '').trim() || '—';
}

export function getStatusRoundLabel(status) {
  if (!status) return null;
  if (status.includes('（1/2）')) return '1 / 2';
  if (status.includes('（2/2）')) return '2 / 2';
  return null;
}

export function isInProgressStatus(status) {
  const s = String(status || '');
  return (
    s.startsWith('日程調整中') ||
    s.startsWith('面談予定') ||
    s.startsWith('面談済み') ||
    s === '回答待ち' ||
    s.startsWith('面談済み／回答待ち')
  );
}

export function isTerminalStatus(status) {
  const normalized = normalizeSalesStatus(status);
  return normalized === '成約' || normalized.startsWith('成約') || normalized === '見送り';
}

export function parseHistoryTimestamp(timestamp) {
  if (!timestamp) return null;
  const parsed = new Date(String(timestamp).replace(' ', 'T'));
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
}

export function timestampToYearMonth(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  return getCurrentYearMonth(d);
}

/** history から指定ステータスへ変わった年月 (YYYY-MM) を取得。フォールバックは created_at のみ */
export function getStatusChangeYearMonth(record, targetStatuses) {
  const targets = targetStatuses.map(normalizeSalesStatus);
  const history = Array.isArray(record.history) ? record.history : [];

  for (let i = history.length - 1; i >= 0; i -= 1) {
    const entry = history[i];
    const hasChange = (entry.changes || []).some((change) => {
      const fieldName = String(change.field || '').trim();
      if (fieldName !== 'ステータス' && fieldName !== 'status') return false;
      return targets.includes(normalizeSalesStatus(change.new));
    });
    if (hasChange && entry.timestamp) {
      return String(entry.timestamp).slice(0, 7);
    }
  }

  if (record.created_at) {
    return String(record.created_at).slice(0, 7);
  }
  return null;
}

export function getStatusChangeTimestamp(record, targetStatuses) {
  const targets = targetStatuses.map(normalizeSalesStatus);
  const history = Array.isArray(record.history) ? record.history : [];

  for (let i = history.length - 1; i >= 0; i -= 1) {
    const entry = history[i];
    const hasChange = (entry.changes || []).some((change) => {
      const fieldName = String(change.field || '').trim();
      if (fieldName !== 'ステータス' && fieldName !== 'status') return false;
      return targets.includes(normalizeSalesStatus(change.new));
    });
    if (hasChange) {
      const time = parseHistoryTimestamp(entry.timestamp);
      if (time) return time;
    }
  }
  return null;
}

/** history からステータス変更日を M/D 形式で取得（成約/見送りカード用） */
export function getStatusChangeDateLine(record, targetStatuses) {
  const targets = targetStatuses.map(normalizeSalesStatus);
  const history = Array.isArray(record.history) ? record.history : [];

  for (let i = history.length - 1; i >= 0; i -= 1) {
    const entry = history[i];
    const hasChange = (entry.changes || []).some((change) => {
      const fieldName = String(change.field || '').trim();
      if (fieldName !== 'ステータス' && fieldName !== 'status') return false;
      return targets.includes(normalizeSalesStatus(change.new));
    });
    if (hasChange && entry.timestamp) {
      const dateStr = String(entry.timestamp).slice(0, 10);
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        return `${Number(parts[1])}/${Number(parts[2])}`;
      }
      return dateStr;
    }
  }

  if (record.created_at) {
    const dateStr = String(record.created_at).slice(0, 10);
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${Number(parts[1])}/${Number(parts[2])}`;
    }
  }
  return '—';
}

/**
 * 営業月カンバン表示判定（PP/BP共通）
 * - 進行中3ステータス: 営業月に関係なく常に表示（月をまたいで維持）
 * - 成約/見送り: ステータス変更月が選択営業月と一致する場合のみ表示
 */
export function shouldShowInSalesMonthKanban(record, salesMonth) {
  const status = normalizeSalesStatus(record.status);
  const rawStatus = String(record.status || '');
  const isWon = status === '成約' || rawStatus.startsWith('成約');
  const isLost = status === '見送り';

  if (isWon || isLost) {
    const targets = isWon ? ['成約'] : ['見送り', 'お見送り'];
    const changeYM = getStatusChangeYearMonth(record, targets);
    const viewMonth = salesMonth === 'all' ? getCurrentYearMonth() : salesMonth;
    if (!changeYM) return viewMonth === getCurrentYearMonth();
    return changeYM === viewMonth;
  }

  return isInProgressStatus(record.status);
}

/** @deprecated エイリアス — shouldShowInSalesMonthKanban を使用 */
export function shouldShowInKanban(record, salesMonth) {
  return shouldShowInSalesMonthKanban(record, salesMonth);
}

/** @deprecated エイリアス — shouldShowInSalesMonthKanban を使用 */
export function shouldShowInPpKanban(record, salesMonth) {
  return shouldShowInSalesMonthKanban(record, salesMonth);
}

/** 実績・プランナー集計: カンバン表示と同じ営業月判定 */
export function shouldCountInSalesMonthStats(record, statsMonth) {
  if (statsMonth === 'all') {
    return isInProgressStatus(record.status) || isTerminalStatus(record.status);
  }
  return shouldShowInSalesMonthKanban(record, statsMonth);
}

/**
 * BPプランナー別件数: 面談日（interview_date）が営業月に該当する案件
 * Kanban用 shouldCountInSalesMonthStats とは別（進行中を月またぎで数えない）
 */
export function shouldCountInPlannerStatsMonth(record, salesMonth) {
  if (salesMonth === 'all') {
    return Boolean(getInterviewYearMonth(record.interview_date));
  }
  return getInterviewYearMonth(record.interview_date) === salesMonth;
}

/** PP 実績セクション: 進行中件数（現在進行中の案件数） */
export function countPpInProgress(records, salesField = null, salesFilter = 'all') {
  return records.filter((record) => {
    if (!isInProgressStatus(record.status)) return false;
    if (salesField && salesFilter !== 'all' && record[salesField] !== salesFilter) return false;
    return true;
  }).length;
}

export function buildKanbanMonthOptions(groupedByMonth = {}, monthCount = 13) {
  const recent = buildRecentMonths(monthCount);
  const options = [];
  if (groupedByMonth['未定']) options.push('未定');
  return [...options, ...recent];
}

/** 面談日から YYYY-MM を取得（interview_date 等） */
export function getInterviewYearMonth(dateValue) {
  if (!dateValue) return null;
  const str = String(dateValue).trim();
  if (!str) return null;
  if (/^\d{4}-\d{2}/.test(str)) return str.slice(0, 7);
  const parsed = new Date(str.includes('T') ? str : `${str}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  return getCurrentYearMonth(parsed);
}

/** 面談実績: 面談日ベースで月一致 */
export function matchesInterviewMonth(record, statsMonth, dateField = 'interview_date') {
  if (statsMonth === 'all') return Boolean(getInterviewYearMonth(record[dateField]));
  return getInterviewYearMonth(record[dateField]) === statsMonth;
}

/** 面談実績: 成約数（ステータス変更月ベース） */
export function countWonInMonth(records, statsMonth, salesField = null, salesFilter = 'all') {
  return records.filter((record) => {
    if (normalizeSalesStatus(record.status) !== '成約' && !String(record.status || '').startsWith('成約')) {
      return false;
    }
    if (salesField && salesFilter !== 'all' && record[salesField] !== salesFilter) return false;
    if (statsMonth === 'all') return true;
    return getStatusChangeYearMonth(record, ['成約']) === statsMonth;
  }).length;
}

/** 面談実績: 総面談数（面談日ベース・record件数） */
export function countInterviewsInMonth(records, statsMonth, salesField = null, salesFilter = 'all') {
  return filterPpInterviewsForStatsMonth(records, statsMonth, salesField, salesFilter).length;
}

/** PP: recordから面談日候補を収集（現在値 + history の「面談日」変更） */
export function extractPpInterviewDateKeys(record) {
  const keys = new Set();
  const addDate = (value) => {
    if (!value || value === '(なし)') return;
    const ym = getInterviewYearMonth(value);
    if (!ym) return;
    keys.add(String(value).trim().slice(0, 10));
  };

  addDate(record.interview_date);
  for (const entry of record.history || []) {
    for (const change of entry.changes || []) {
      const field = String(change.field || '').trim();
      if (field === '面談日' || field === 'interview_date') {
        addDate(change.new);
      }
    }
  }
  return keys;
}

/** PP実績: 選択月の面談イベント一覧（1 record × 面談日 = 1件） */
export function getPpInterviewEventsForMonth(records, statsMonth, salesField = null, salesFilter = 'all') {
  const events = [];
  records.forEach((record) => {
    if (salesField && salesFilter !== 'all' && record[salesField] !== salesFilter) return;
    extractPpInterviewDateKeys(record).forEach((dateKey) => {
      const ym = getInterviewYearMonth(dateKey);
      if (statsMonth === 'all' || ym === statsMonth) {
        events.push({ record, dateKey });
      }
    });
  });
  return events;
}

/** PP実績: 選択月の面談record一覧（後方互換・イベントの record を返す） */
export function filterPpInterviewsForStatsMonth(records, statsMonth, salesField = null, salesFilter = 'all') {
  const seen = new Set();
  const result = [];
  getPpInterviewEventsForMonth(records, statsMonth, salesField, salesFilter).forEach(({ record, dateKey }) => {
    const key = `${record.id}:${dateKey}`;
    if (seen.has(key)) return;
    seen.add(key);
    result.push(record);
  });
  return result;
}

/** PP実績: エンジニア識別子（APIに engineer_id が無いため名前を優先、空なら record.id） */
export function getPpEngineerKey(record) {
  const name = String(record.engineer_name || '').trim();
  if (name) return name;
  if (record.id != null) return `id:${record.id}`;
  return null;
}

/** PP実績: 総面談数・対象エンジニアを同一母集団（面談イベント）から算出 */
export function getPpInterviewStatsForMonth(records, statsMonth, salesField = null, salesFilter = 'all') {
  const events = getPpInterviewEventsForMonth(records, statsMonth, salesField, salesFilter);
  const engineerKeys = new Set();
  events.forEach(({ record }) => {
    const key = getPpEngineerKey(record);
    if (key) engineerKeys.add(key);
  });
  return {
    monthlyInterviews: events,
    totalInterviews: events.length,
    uniqueEngineers: engineerKeys.size,
  };
}

/** BP プランナー別件数（0.5件単位・面談日の営業月基準） */
export function calculatePlannerStats(records, planners, salesMonth) {
  const stats = {};
  planners.forEach((planner) => {
    stats[planner] = { main: 0, support: 0, total: 0 };
  });

  records
    .filter((record) => shouldCountInPlannerStatsMonth(record, salesMonth))
    .forEach((prospect) => {
      if (stats[prospect.main_planner]) {
        stats[prospect.main_planner].main += 0.5;
      }
      (prospect.support_planners || []).forEach((sp) => {
        if (stats[sp]) {
          stats[sp].support += 0.5;
        }
      });
    });

  Object.keys(stats).forEach((planner) => {
    stats[planner].total = stats[planner].main + stats[planner].support;
  });

  return stats;
}

/** 面談実績: 進行中（面談日ベース + ステータス） */
export function countInProgressInMonth(records, statsMonth, salesField = null, salesFilter = 'all') {
  return records.filter((record) => {
    if (!isInProgressStatus(record.status)) return false;
    if (salesField && salesFilter !== 'all' && record[salesField] !== salesFilter) return false;
    if (statsMonth === 'all') return true;
    return matchesInterviewMonth(record, statsMonth);
  }).length;
}

/** BP ステータス別件数（実績月: 進行中=面談日、成約/見送り=変更月） */
export function getStatusCountsForStatsMonth(records, statsMonth) {
  const counts = {
    '日程調整中': 0,
    '面談予定': 0,
    '面談済み／回答待ち': 0,
    '成約': 0,
    '見送り': 0,
  };

  const normalizeKey = (s) => {
    if (!s) return '';
    if (s.startsWith('日程調整中')) return '日程調整中';
    if (s.startsWith('面談予定')) return '面談予定';
    if (s.startsWith('面談済み') || s === '回答待ち' || s.startsWith('面談済み／回答待ち')) {
      return '面談済み／回答待ち';
    }
    if (s === '成約' || s.startsWith('成約')) return '成約';
    if (s === '見送り' || s === 'お見送り') return '見送り';
    return '';
  };

  records.forEach((record) => {
    const key = normalizeKey(record.status);
    if (!key || counts[key] === undefined) return;

    if (key === '成約') {
      if (statsMonth !== 'all' && getStatusChangeYearMonth(record, ['成約']) !== statsMonth) return;
    } else if (key === '見送り') {
      if (statsMonth !== 'all' && getStatusChangeYearMonth(record, ['見送り', 'お見送り']) !== statsMonth) {
        return;
      }
    }
    // 進行中3ステータス: 現在値（営業月でリセットしない）

    counts[key] += 1;
  });

  return counts;
}
